/**
 * @author NikolaV
 */
import { Ajax, Ajax2 } from '@shared/Ajax';

function fetchGroups(organizationUuid: string, projectUuid: string): Promise<IUserGroup[]> {
    const data = {
        projectUuid,
        organizationUuid,
    };

    if (organizationUuid) data.organizationUuid = organizationUuid;
    if (projectUuid) data.projectUuid = projectUuid;

    return Ajax2({
        url: kuura.getIamAddress() + '/groups',
        useToken: true,
        method: 'get',
        data,
    }).then(resp => {
        if (Array.isArray(resp)) {
            resp.forEach(group => {
                group.members = Array.isArray(group.members) ? group.members : [];
                group.roles = Array.isArray(group.roles) ? group.roles : [];
            });

            // remove default/root project group, it should never be used or visible
            resp = resp.filter(g => g.uuid !== g.projectUuid);
        }
        return resp;
    });
}

function fetchGroup(groupUuid: string): Promise<IUserGroup> {
    return Ajax2({
        url: kuura.getIamAddress() + '/group/' + groupUuid,
        useToken: true,
        method: 'get',
    });
}

function fetchUserGroups(userUuid: string, organizationUuid: string, projectUuid: string): Promise<IUserGroup[]> {
    const data = {
        projectUuid,
        organizationUuid,
    };

    if (organizationUuid) data.organizationUuid = organizationUuid;
    if (projectUuid) data.projectUuid = projectUuid;

    return Ajax2({
        url: kuura.getIamAddress() + '/user/' + userUuid + '/groups',
        useToken: true,
        method: 'get',
        data,
    }).then(resp => {
        //todo - check does we need this filters
        if (Array.isArray(resp)) {
            resp.forEach(group => {
                group.users = Array.isArray(group.users) ? group.users : [];
                group.roles = Array.isArray(group.roles) ? group.roles : [];
            });

            // remove default/root project group, its should never be used or visible
            resp = resp.filter(g => g.uuid !== g.projectUuid);
        }
        return resp;
    });
}

function fetchGroupsFromKuura(projectId: number): JQueryPromise<IUserGroup[]> {
    return Ajax({
        url: `/api/1/groups.json`,
        method: 'get',
        data: {
            projectId,
        },
    }).then(resp => resp.groups.reverse());
}

function fetchKuuraUserGroups(): Promise<IUserGroup[]> {
    return Ajax2({
        url: `/kuura/v1/groups`,
        useToken: true,
        method: 'get',
    }).then(resp => resp);
}

function createGroup(name: string, organizationUuid: string, projectUuid: string): Promise<IUserGroup> {
    const data: any = {
        name,
        users: [],
        roles: [],
    };

    if (organizationUuid) data.organizationUuid = organizationUuid;
    if (projectUuid) data.projectUuid = projectUuid;

    return Ajax2({
        url: kuura.getIamAddress() + '/group',
        useToken: true,
        method: 'post',
        contentType: 'json',
        data,
    }).then(resp => resp.group);
}

function renameGroup(groupUuid: string, name: string): Promise<void> {
    return Ajax2({
        url: kuura.getIamAddress() + '/group/' + groupUuid,
        useToken: true,
        method: 'patch',
        contentType: 'json',
        data: [
            {
                op: 'replace',
                path: '/name',
                value: name,
            },
        ],
    });
}

function updateGroup(groupUuid: string, userUuids?: string[], roleUuids?: string[]) {
    return Ajax2({
        url: kuura.getIamAddress() + '/group/' + groupUuid,
        useToken: true,
        method: 'patch',
        contentType: 'json',
        data: [
            { op: 'replace', path: '/members', value: userUuids },
            { op: 'replace', path: '/roles', value: roleUuids },
        ],
    });
}

function deleteGroup(groupUuid: string): Promise<void> {
    return Ajax2({
        url: kuura.getIamAddress() + '/group/' + groupUuid,
        useToken: true,
        method: 'delete',
    });
}

export const UserGroupsService = {
    fetchGroups,
    fetchGroup,
    fetchUserGroups,
    fetchKuuraUserGroups,
    createGroup,
    updateGroup,
    renameGroup,
    deleteGroup,
    fetchGroupsFromKuura,

    editGroupAddUsers: (groupUuid: string, userUuids?: string[]) => {
        return Ajax2({
            url: kuura.getIamAddress() + '/group/' + groupUuid + '/users',
            useToken: true,
            method: 'put',
            contentType: 'json',
            data: userUuids,
        });
    },
    editGroupRemoveUsers: (groupUuid: string, userUuids?: string[]) => {
        return Ajax2({
            url: kuura.getIamAddress() + '/group/' + groupUuid + '/users',
            useToken: true,
            method: 'delete',
            contentType: 'json',
            data: userUuids,
        });
    },
    editGroupAddRoles: (groupUuid: string, roleUuids?: string[]) => {
        return Ajax2({
            url: kuura.getIamAddress() + '/group/' + groupUuid + '/roles',
            useToken: true,
            method: 'put',
            contentType: 'json',
            data: roleUuids,
        });
    },
    editGroupRemoveRoles: (groupUuid: string, roleUuids?: string[]) => {
        return Ajax2({
            url: kuura.getIamAddress() + '/group/' + groupUuid + '/roles',
            useToken: true,
            method: 'delete',
            contentType: 'json',
            data: roleUuids,
        });
    },

    saveUserLog: (projectUuid: string, groupUuid: string, action: string, roleUuids: string[], userUuids: string[]) => {
        const resp = Ajax2({
            url: '/kuura/v1/saveUserLog',
            useToken: true,
            method: 'post',
            contentType: 'urlencoded',
            data: {
                projectUuid,
                groupUuid,
                action,
                roleUuids: roleUuids.toString(),
                userUuids: userUuids.toString(),
            },
        });

        return resp;
    },
};
